import type { Nullable, RecordKey } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { doNothing } from '@ricoh/ria.client.core/constants/core/action';
import { ANIMATION_DELAY } from '@ricoh/ria.client.core/constants/ui/animation';

import type { PreventDoubleClickHook, UseEventOptions, UseEventsOptions, UseWindowEventOptions } from './event.type';

/**
 * Use bind event.
 *
 * @template TRef Ref type.
 * @template TElement Element type.
 * @template TEventName Event name.
 * @param {UseEventOptions} options Options.
 */
export function useEvent<TRef, TElement extends HTMLElement, TEventName extends keyof HTMLElementEventMap>({
  type,
  handle,
  shouldBind = true,
  ...options
}: UseEventOptions<TRef, TElement, TEventName>) {
  useEffect(() => {
    if (!shouldBind) {
      return doNothing;
    }

    let element;
    if ('selector' in options) {
      element = options.selector(options.ref);
    } else if ('ref' in options) {
      element = options.ref.current;
    } else {
      ({ element } = options);
    }

    if (!element) {
      return doNothing;
    }

    element.addEventListener(type, handle);

    return () => {
      element.removeEventListener(type, handle);
    };
  }, [handle, options, shouldBind, type]);
}

/**
 * Use bind events.
 *
 * @template TKEy Key type.
 * @template TElement Element type.
 * @template TEventName Event name.
 * @param {UseEventOptions} options Options.
 */
export function useEvents<TKEy extends RecordKey, TElement extends HTMLElement, TEventName extends keyof HTMLElementEventMap>({
  type,
  refs,
  handles,
  shouldBind = true,
}: UseEventsOptions<TKEy, TElement, TEventName>) {
  useEffect(() => {
    if (!shouldBind) {
      return doNothing;
    }

    const handleMapping = handles(refs);

    handleMapping.forEach(({ element, handle }) => {
      if (!element) {
        return;
      }

      element.addEventListener(type, handle);
    });

    return () => {
      handleMapping.forEach(({ element, handle }) => {
        if (!element) {
          return;
        }

        element.removeEventListener(type, handle);
      });
    };
  }, [handles, refs, shouldBind, type]);
}

/**
 * Use bind window event.
 *
 * @template TEventName Event name.
 * @param {UseWindowEventOptions} options Options.
 */
export function useWindowEvent<TEventName extends keyof WindowEventMap>({ type, handle, shouldBind = true }: UseWindowEventOptions<TEventName>) {
  useEffect(() => {
    if (!shouldBind) {
      return doNothing;
    }

    globalThis.addEventListener(type, handle);

    return () => {
      globalThis.removeEventListener(type, handle);
    };
  }, [handle, shouldBind, type]);
}

/**
 * Use prevent double click.
 *
 * @param {number} delay Delay time.
 *
 * @returns {PreventDoubleClickHook} PreventDoubleClick Hook.
 */
export function usePreventDoubleClick(delay: number = ANIMATION_DELAY.EXTRA_FAST): PreventDoubleClickHook {
  const disableClickRef = useRef<boolean>(false);
  const timeoutRef = useRef<Nullable<NodeJS.Timeout>>(null);

  const doClick = useCallback<Func<[action: Func]>>(
    (action) => {
      timeoutRef.current = setTimeout(() => {
        if (!disableClickRef.current) {
          action();
        }

        disableClickRef.current = false;
      }, delay);
    },
    [delay],
  );

  const doDisableClick = useCallback<Func<[action: Func]>>((action) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);

      disableClickRef.current = true;
    }

    action();
  }, []);

  return useMemo<PreventDoubleClickHook>(
    () => ({
      doClick,
      doDisableClick,
    }),
    [doClick, doDisableClick],
  );
}
